import { prefersLessMotion } from '@utils/calendar/markUsedCelebration';
import { WEEK_TIME_COLORS } from '@utils/weekTimeBudget';

/** Marks the "Estimate Tasks" button in the top bar, where the "Estimate tasks" box flies when it is closed. */
export const ESTIMATE_TASKS_BUTTON_ATTRIBUTE = 'data-estimate-tasks-button';
/** Marks the red count on it, which pulses as the box lands. */
export const ESTIMATE_TASKS_COUNT_ATTRIBUTE = 'data-estimate-tasks-count';

const FLIGHT_MS = 700;
const HINT_MS = 3000;
// Points along the curve; the browser draws straight between them, which at this many reads as the curve.
const PATH_STEPS = 32;

const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

/**
 * A small dark bubble under the button saying where the box went, for a few seconds (Petr: so students "know where to
 * find it again"). Goes early if the button or the bubble is clicked.
 */
const showHint = (button: HTMLElement, text: string, delay: number) => {
  const target = button.getBoundingClientRect();
  const bubble = document.createElement('div');
  bubble.textContent = text;
  Object.assign(bubble.style, {
    position: 'fixed',
    top: `${target.bottom + 10}px`,
    left: `${target.left + target.width / 2}px`,
    transform: 'translateX(-50%)',
    maxWidth: '220px',
    padding: '8px 12px',
    borderRadius: '6px',
    background: 'var(--color-dark)',
    color: 'white',
    fontSize: '13px',
    fontWeight: '600',
    lineHeight: '1.35',
    textAlign: 'center',
    boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
    zIndex: '10001',
    opacity: '0',
    cursor: 'pointer'
  });
  const arrow = document.createElement('div');
  Object.assign(arrow.style, {
    position: 'absolute',
    top: '-6px',
    left: '50%',
    marginLeft: '-6px',
    borderLeft: '6px solid transparent',
    borderRight: '6px solid transparent',
    borderBottom: '6px solid var(--color-dark)'
  });
  bubble.appendChild(arrow);
  document.body.appendChild(bubble);

  let gone = false;
  const remove = () => {
    if (gone) return;
    gone = true;
    bubble.remove();
    button.removeEventListener('click', remove);
  };
  bubble.addEventListener('click', remove);
  button.addEventListener('click', remove);
  if (typeof bubble.animate !== 'function') {
    bubble.style.opacity = '1';
    window.setTimeout(remove, delay + HINT_MS);
    return;
  }
  bubble
    .animate(
      [
        { opacity: 0, transform: 'translate(-50%, -4px)' },
        { opacity: 1, transform: 'translate(-50%, 0)', offset: 0.08 },
        { opacity: 1, transform: 'translate(-50%, 0)', offset: 0.9 },
        { opacity: 0, transform: 'translate(-50%, 0)' }
      ],
      { duration: HINT_MS, delay, fill: 'backwards', easing: 'ease-out' }
    )
    .finished.then(remove, remove);
};

/**
 * Flies `ghost`, a copy of something about to go, from `from` in an arc up into `button`, shrinking to the button's size
 * and fading as it arrives; the button then bumps, an aqua ring spreads off it, its count pulses, and a hint says what
 * landed there. For the "Estimate tasks" box, so closing it shows where it went (Petr: a nice animation "they'll
 * notice"). Worked out up front as one animation per element, which the browser runs on its own, as the mark-used dot
 * does. A student who asks for less motion gets only the hint.
 */
export const flyIntoButton = (ghost: HTMLElement, from: DOMRect, button: HTMLElement, hint: string) => {
  const end = button.getBoundingClientRect();
  if (prefersLessMotion() || typeof ghost.animate !== 'function' || !end.width || !from.width) {
    showHint(button, hint, 0);
    return;
  }

  Object.assign(ghost.style, {
    position: 'fixed',
    left: `${from.left}px`,
    top: `${from.top}px`,
    right: 'auto',
    bottom: 'auto',
    width: `${from.width}px`,
    height: `${from.height}px`,
    margin: '0',
    boxSizing: 'border-box',
    transformOrigin: 'center center',
    pointerEvents: 'none',
    zIndex: '10000',
    willChange: 'transform, opacity'
  });
  document.body.appendChild(ghost);

  const x0 = from.left + from.width / 2;
  const y0 = from.top + from.height / 2;
  const x1 = end.left + end.width / 2;
  const y1 = end.top + end.height / 2;
  // Lifts straight up first, then bends across into the button.
  const cx = x0 - (x1 - x0) * 0.1;
  const cy = y1 + (y0 - y1) * 0.25;
  const endScaleX = end.width / from.width;
  const endScaleY = end.height / from.height;

  ghost
    .animate(
      Array.from({ length: PATH_STEPS + 1 }, (_, index) => {
        const t = index / PATH_STEPS;
        const p = easeInOut(t);
        const x = (1 - p) * (1 - p) * x0 + 2 * (1 - p) * p * cx + p * p * x1;
        const y = (1 - p) * (1 - p) * y0 + 2 * (1 - p) * p * cy + p * p * y1;
        // Puffs up a touch as it lifts off, then shrinks into the button; tilts a little on the way.
        const lift = t < 0.15 ? 1 + t * 0.4 : 1.06;
        const scaleX = lift + (endScaleX - lift) * p;
        const scaleY = lift + (endScaleY - lift) * p;
        const tilt = Math.sin(t * Math.PI) * -4;
        return {
          offset: t,
          transform: `translate(${x - x0}px, ${y - y0}px) rotate(${tilt}deg) scale(${scaleX}, ${scaleY})`,
          opacity: t > 0.85 ? (1 - t) / 0.15 : 1
        };
      }),
      { duration: FLIGHT_MS, easing: 'linear' }
    )
    .finished.then(
      () => ghost.remove(),
      () => ghost.remove()
    );

  const landsAt = FLIGHT_MS - 60;
  button.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }], {
    duration: 380,
    delay: landsAt,
    easing: 'ease-out'
  });
  const ring = document.createElement('div');
  Object.assign(ring.style, {
    position: 'fixed',
    left: `${end.left}px`,
    top: `${end.top}px`,
    width: `${end.width}px`,
    height: `${end.height}px`,
    borderRadius: getComputedStyle(button).borderRadius,
    border: `2px solid ${WEEK_TIME_COLORS.usedStudy}`,
    boxSizing: 'border-box',
    pointerEvents: 'none',
    zIndex: '10000',
    opacity: '0'
  });
  document.body.appendChild(ring);
  ring
    .animate(
      [
        { transform: 'scale(1)', opacity: 1 },
        { transform: 'scale(1.35, 1.8)', opacity: 0 }
      ],
      { duration: 520, delay: landsAt, easing: 'ease-out' }
    )
    .finished.then(
      () => ring.remove(),
      () => ring.remove()
    );
  const count = button.querySelector<HTMLElement>(`[${ESTIMATE_TASKS_COUNT_ATTRIBUTE}]`);
  count?.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.45)' }, { transform: 'scale(1)' }], {
    duration: 420,
    delay: landsAt + 80,
    easing: 'ease-out'
  });

  showHint(button, hint, landsAt + 150);
};
